import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, Wallet } from 'lucide-react';
import { playClickSound } from '@/utils/soundUtils';

interface GameProvider {
    id: string;
    name: string;
    icon: string;
}

interface OrderSummaryModalProps {
    isOpen: boolean;
    onClose: () => void;
    totalAmount: number;
    providerAmounts: Map<string, number>;
    providers: GameProvider[];
    onIncrement: (providerId: string) => void;
    onDecrement: (providerId: string) => void;
    onRemoveItem: (providerId: string) => void;
    selectedProvider?: GameProvider | null;
    selectedAction?: 'deposit' | 'withdrawal' | null;
    onConfirm?: () => void;
}

const OrderSummaryModal: React.FC<OrderSummaryModalProps> = ({
    isOpen,
    onClose,
    totalAmount,
    providerAmounts,
    providers,
    onIncrement,
    onDecrement,
    onRemoveItem,
    selectedProvider,
    selectedAction,
    onConfirm,
}) => {
    const navigate = useNavigate();

    if (!isOpen) return null;

    // Check if there are any items in the order
    const hasItems = Array.from(providerAmounts.values()).some(amount => amount > 0);

    // Overlay colors for provider icons (matching Figma design)
    const overlayColors = [
        'rgba(168, 85, 247, 0.2)', // Purple for Golden Treasure
        'rgba(6, 182, 212, 0.2)',  // Cyan for VegasX
        'rgba(16, 185, 129, 0.2)', // Green for Egame99
    ];

    const handleConfirmDeposit = () => {
        playClickSound();

        // Save provider amounts to sessionStorage
        const amountsArray = Array.from(providerAmounts.entries())
            .filter(([_, amount]) => amount > 0)
            .map(([providerId, amount]) => ({ providerId, amount }));

        if (amountsArray.length > 0) {
            const pendingData = {
                amounts: amountsArray,
                timestamp: Date.now(),
                source: 'confirm_deposit'
            };

            try {
                sessionStorage.setItem('pending_deposit_amounts', JSON.stringify(pendingData));
                console.log('💾 Saved pending deposit amounts to sessionStorage:', pendingData);
            } catch (error) {
                console.error('Failed to save to sessionStorage:', error);
            }
        }

        // Close popup
        onClose();

        // Call custom confirm handler if provided
        if (onConfirm) {
            onConfirm();
            return;
        }

        // Navigate to login page with provider and action info
        const params = new URLSearchParams();
        if (selectedProvider) {
            params.set('providerId', String(selectedProvider.id));
            params.set('providerName', selectedProvider.name);
        }
        if (selectedAction) {
            params.set('action', selectedAction);
        }
        if (totalAmount > 0) {
            params.set('totalAmount', String(totalAmount));
        }
        navigate(`/login?${params.toString()}`);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-3 sm:px-4">
            {/* Semi-transparent background */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={() => {
                    playClickSound();
                    onClose();
                }}
            />

            {/* Popup Content - compact size */}
            <div
                className={`relative z-50 flex flex-col overflow-hidden rounded-2xl sm:rounded-3xl border border-white/15 bg-[rgba(32,46,81,0.85)] backdrop-blur-[16px] w-full max-w-[calc(100vw-24px)] max-h-[85vh] min-h-0 ${hasItems ? 'sm:max-w-[360px] md:max-w-[400px]' : 'sm:max-w-[340px] sm:w-[340px] sm:min-h-[380px] sm:h-[380px]'}`}
                style={{
                    boxShadow:
                        '0px 20px 40px -12px rgba(0, 0, 0, 0.5), inset 0px 0px 20px 1px rgba(139, 92, 246, 0.12)',
                    boxSizing: 'border-box',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header - compact */}
                <div className="flex flex-col items-center px-3 sm:px-4 pt-3 sm:pt-4 pb-2 gap-1.5 flex-shrink-0">
                    <h3
                        className="text-white text-center font-inter italic font-black text-lg sm:text-xl leading-tight"
                        style={{ fontFamily: 'Inter', fontStyle: 'italic', fontWeight: 900 }}
                    >
                        Order Summary
                    </h3>
                    <div
                        className="w-16 h-0.5 rounded-full shrink-0"
                        style={{ background: 'linear-gradient(90deg, #06B6D4 0%, #A855F7 50%, #EC4899 100%)' }}
                    />
                </div>

                {/* Scrollable Items List - compact */}
                <div className={`flex flex-col w-full ${hasItems ? 'overflow-y-auto px-3 sm:px-4 pt-0 pb-2 sm:pb-3 space-y-0 flex-1 min-h-0' : ''}`}>
                    {hasItems ? (
                        Array.from(providerAmounts.entries())
                            .filter(([_, amount]) => amount > 0)
                            .map(([providerId, amount], index, array) => {
                                const provider = providers.find(p => p.id === providerId);
                                if (!provider) return null;
                                const isLast = index === array.length - 1;

                                return (
                                    <div key={providerId} className="w-full">
                                        <div className="flex items-center justify-between w-full max-w-full mx-auto py-2 sm:py-2.5 gap-1.5 sm:gap-2">
                                            {/* Provider Icon + Name */}
                                            <div className="flex items-center flex-1 min-w-0">
                                                <div
                                                    className="flex justify-center items-center w-7 h-7 sm:w-8 sm:h-8 rounded-md border border-white/20 shrink-0"
                                                    style={{
                                                        background: overlayColors[index % overlayColors.length],
                                                    }}
                                                >
                                                    <img
                                                        src={provider.icon}
                                                        alt={provider.name}
                                                        className="w-5 h-5 sm:w-6 sm:h-6 object-contain rounded"
                                                        onError={(e) => {
                                                            const target = e.target as HTMLImageElement;
                                                            target.src = '/icons/egame.jpg';
                                                        }}
                                                    />
                                                </div>
                                                <div className="pl-1.5 sm:pl-2 min-w-0 flex-1">
                                                    <p className="text-white font-black text-[10px] sm:text-xs truncate">
                                                        {provider.name}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Amount Control */}
                                            <div className="flex items-center gap-0.5 sm:gap-1.5 flex-shrink-0">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        playClickSound();
                                                        onDecrement(providerId);
                                                    }}
                                                    disabled={amount <= 0}
                                                    className="flex justify-center items-center w-6 h-6 sm:w-7 sm:h-7 rounded border border-white/10 bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                                                >
                                                    <Minus className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                                                </button>

                                                <div className="flex justify-center px-1 sm:px-1.5">
                                                    <div className="min-w-[52px] sm:min-w-[60px] h-6 sm:h-7 flex items-center justify-center rounded-full border border-emerald-500/80 bg-emerald-500/10 shadow-[0_0_8px_rgba(16,185,129,0.2)] px-1.5 sm:px-2">
                                                        <span
                                                            style={{ fontFamily: 'JetBrains Mono, ui-monospace' }}
                                                            className="text-[10px] sm:text-xs leading-tight font-extrabold text-emerald-400 text-center whitespace-nowrap"
                                                        >
                                                            ${Math.round(amount).toFixed(2)}
                                                        </span>
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        playClickSound();
                                                        onIncrement(providerId);
                                                    }}
                                                    disabled={amount >= 10000}
                                                    className="flex justify-center items-center w-6 h-6 sm:w-7 sm:h-7 rounded border border-white/10 bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                                                >
                                                    <Plus className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                                                </button>
                                            </div>

                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onRemoveItem(providerId);
                                                }}
                                                className="shrink-0 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded hover:bg-red-500/20 transition-colors ml-0.5 sm:ml-1"
                                            >
                                                <Trash2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white/70" />
                                            </button>
                                        </div>
                                        {!isLast && (
                                            <div
                                                className="w-full h-[0.7px] mx-auto"
                                                style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                                            />
                                        )}
                                    </div>
                                );
                            })
                    ) : (
                        /* Empty State - compact */
                        <div className="flex flex-col items-center w-full px-3 sm:px-4 pb-3 gap-0 min-h-0 overflow-y-auto">
                            <div className="flex flex-col items-center gap-2 sm:gap-3 w-full max-w-[220px]">
                                <div
                                    className="flex justify-center items-center w-16 h-16 sm:w-20 sm:h-20 rounded-full shrink-0"
                                    style={{
                                        background: 'radial-gradient(70.71% 70.71% at 50% 50%, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0) 70%)',
                                        border: '1px solid rgba(255, 255, 255, 0.05)',
                                    }}
                                >
                                    <Wallet className="w-8 h-8 sm:w-10 sm:h-10" style={{ color: 'rgba(192, 132, 252, 0.8)' }} />
                                </div>
                                <p
                                    className="text-white text-center font-black text-xs sm:text-sm uppercase tracking-wider"
                                    style={{ fontFamily: 'Inter' }}
                                >
                                    NO GAMES SELECTED
                                </p>
                                <p className="text-center text-white/60 text-[11px] sm:text-xs leading-snug max-w-[220px]">
                                    Go back and select some games to fund your adventure.
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Total Deposit - compact */}
                <div
                    className="w-full flex flex-row justify-between items-center px-3 sm:px-4 py-2 sm:py-3 flex-shrink-0 border-t border-white/10 bg-white/[0.04]"
                >
                    <p className="text-[10px] sm:text-xs font-black text-white/50" style={{ fontFamily: 'Inter' }}>
                        Total Deposit
                    </p>
                    <p
                        className="font-extrabold text-base sm:text-lg tabular-nums"
                        style={{
                            fontFamily: 'JetBrains Mono',
                            color: hasItems ? '#FFCC00' : 'rgba(255, 255, 255, 0.2)',
                        }}
                    >
                        ${Math.round(totalAmount).toFixed(2)}
                    </p>
                </div>

                {/* Footer - compact buttons */}
                <div className="flex flex-row justify-center items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 flex-shrink-0 w-full">
                    <button
                        onClick={() => {
                            playClickSound();
                            onClose();
                        }}
                        className="flex justify-center items-center min-w-[64px] sm:min-w-[72px] h-8 sm:h-9 rounded-xl border border-white/10 font-black text-[11px] sm:text-xs text-white/60 px-3"
                        style={{ fontFamily: 'Inter' }}
                    >
                        Close
                    </button>
                    <button
                        onClick={handleConfirmDeposit}
                        disabled={!hasItems}
                        className={`flex-1 flex justify-center items-center gap-1 h-8 sm:h-9 rounded-xl px-3 disabled:opacity-40 disabled:cursor-not-allowed text-xs sm:text-sm font-black ${hasItems ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-[0_0_12px_rgba(16,185,129,0.35),inset_0_0_4px_rgba(255,255,255,0.15)]' : 'bg-white/70 text-white'}`}
                        style={{ fontFamily: 'Inter' }}
                    >
                        <span className="whitespace-nowrap">Confirm Deposit</span>
                        {hasItems && (
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-4 shrink-0" viewBox="0 0 20 24" fill="none">
                                <path d="M8.79159 17.1668L13.1041 12.0002H9.77075L10.3749 7.27099L6.52075 12.8335H9.41659L8.79159 17.1668ZM6.66659 20.3335L7.49992 14.5002H3.33325L10.8333 3.66683H12.4999L11.6666 10.3335H16.6666L8.33325 20.3335H6.66659Z" fill="currentColor" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OrderSummaryModal;
